import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { RadioComponent } from '../../components/radio/radio.component';
import { FileUploaderComponent } from '../../components/file-uploader/file-uploader.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { GlobalService } from '../../services/global.service';
import { ProjectSettingsService } from '../../services/project-settings.service';
import { DropdownService } from '../../services/dropdown.service';
import { ValidationService } from '../../services/validation.service';

@Component({
  selector: 'app-project-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HeaderComponent,
    TextInputComponent,
    RadioComponent,
    FileUploaderComponent,
    SelectInputComponent,
  ],
  templateUrl: './project-settings.component.html',
})
export class ProjectSettingsComponent {
  form: FormGroup;
  isSubmitting = false;
  logo_preview: string | null = null;
  university_options: any[] = [];


  constructor(private fb: FormBuilder, public gs: GlobalService, public ps: ProjectSettingsService, public ds: DropdownService, private router: Router, public vs: ValidationService) {
    this.form = this.fb.group({
      university_id: [null, Validators.required],
      university_name: ['', Validators.required],
      logo_url: ['', Validators.required],
      address_line1: ['', [Validators.required, Validators.minLength(this.vs.min_length_address), Validators.maxLength(this.vs.max_length_address)]],
      address_line2: ['', [Validators.required, Validators.minLength(this.vs.min_length_address), Validators.maxLength(this.vs.max_length_address)]],
      gst_required: ['no', Validators.required],
      gstn: [''],
      signatory_name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern(this.vs.name_pattern)]],
      designation: ['', [Validators.required, Validators.maxLength(100)]],
      signature_url: ['', Validators.required],
    });

    this.form.get('gst_required')?.valueChanges.subscribe((val: string) => {
      const gstn_ctrl = this.form.get('gstn');
      if (val === 'yes') {
        gstn_ctrl?.setValidators([Validators.required]);
      } else {
        gstn_ctrl?.clearValidators();
        gstn_ctrl?.setValue('');
      }
      gstn_ctrl?.updateValueAndValidity();
    });
  }

  async ngOnInit() {
    await this.ds.get_dropdown_list(['university']);
    this.university_options = this.ds.dropdown_list?.university_list || [];

    // belongs_to lives on the Role, not the user row — gs.access_scope.is_all reads the right place.
    if (this.gs.access_scope.is_all) {
      // Super admin selects university via dropdown — no auto-load
      return;
    }

    const university_id = this.gs.user_data?.university_id;
    if (university_id) {
      this.form.patchValue({ university_id });
      await this.load_config(university_id);
    }
    const university_name = this.gs.user_data?.University?.name || this.gs.user_data?.university_name;
    if (university_name) this.form.patchValue({ university_name });
  }

  async on_university_change(event: any) {
    // valueChange emits { value: selectedOptionObject, depended: ... }
    const university_id = event?.value?.value;
    this.form.patchValue({ university_name: '', logo_url: '', address_line1: '', address_line2: '', gst_required: 'no', gstn: '', signatory_name: '', designation: '', signature_url: '' });
    this.logo_preview = null;
    if (!university_id) return;
    const selected = this.university_options.find((u: any) => u.value == university_id);
    if (selected) this.form.patchValue({ university_name: selected.title });
    await this.load_config(university_id);
  }

  async load_config(university_id: any) {
    try {
      const res = await this.ps.get_config(university_id);
      if (res?.data) {
        const config = res.data;
        const address_parts = (config.address || '').split('\n');
        this.form.patchValue({
          logo_url: config.logo_url || '',
          address_line1: address_parts[0] || '',
          address_line2: address_parts[1] || '',
          gst_required: config.gst_enabled ? 'yes' : 'no',
          gstn: config.gstn || '',
          signatory_name: config.signatory_name || '',
          designation: config.designation || '',
          signature_url: config.signature_url || '',
        });
        if (config.logo_url) this.logo_preview = config.logo_url;
      }
    } catch { }
  }

  on_logo_uploaded(event: any) {
    const url = event?.url || event?.file_path || event?.data?.url;
    if (url) {
      const full_url = url.startsWith('http') ? url : `${this.ps.hs.base_url}${url}`;
      this.form.patchValue({ logo_url: full_url });
      this.logo_preview = full_url;
    }
  }

  on_signature_uploaded(event: any) {
    const url = event?.url || event?.file_path || event?.data?.url;
    if (url) {
      const full_url = url.startsWith('http') ? url : `${this.ps.hs.base_url}${url}`;
      this.form.patchValue({ signature_url: full_url });
    }
  }

  get gst_required(): string {
    return this.form.get('gst_required')?.value || 'no';
  }



  async submit() {
    // if (this.isSubmitting) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
      return;
    }
    try {
      this.isSubmitting = true;
      const v = this.form.getRawValue();
      const address_parts = [v.address_line1, v.address_line2].filter(Boolean);
      const payload: any = {
        university_id: v.university_id,
        logo_url: v.logo_url,
        address: address_parts.join('\n'),
        gst_enabled: v.gst_required === 'yes',
        signatory_name: v.signatory_name,
        designation: v.designation,
        signature_url: v.signature_url,
      };
      if (v.gst_required === 'yes') payload.gstn = v.gstn;
      await this.ps.save_config(payload);
      this.gs.toastr_shows_function('Configuration saved successfully.', 'Success', 'success');
      this.gs.go_back();
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to save settings.', 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }
}
